import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { chooseSelectedInquiryTypeSpecificValue } from 'shared/chooseSelectedInquiryTypeSpecificComponent';
import { getGoogleAdsConsent, onGoogleAdsConsentChange } from 'utils/consent/googleAdsConsent';
import { persistGclid } from 'utils/sessionStorage/helpers';
import {
  SOURCE_DATA_SESSION_STORAGE_KEY,
  SPECIAL_LOAN_EMERGENCY_SERVICES_PARAM,
  UTM_DATA_SESSION_STORAGE_KEY,
} from 'utils/sessionStorage/keys';

const GCLID_PARAM = 'bfs_gclid';

// The gclid is captured separately (consent-gated below), so it is not part of the params that
// are stored unconditionally.
const TRACKED_UTM_PARAMETERS = chooseSelectedInquiryTypeSpecificValue({
  [InquiryType.default]: [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ] as const,
});

export type UtmData = Partial<
  Record<(typeof TRACKED_UTM_PARAMETERS)[number] | 'bfs_gclid', string>
>;

export const useCaptureQueryParams = () => {
  const { search } = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);

    sessionStorage.removeItem(UTM_DATA_SESSION_STORAGE_KEY);
    const utmParameters: UtmData = {};
    for (const key of TRACKED_UTM_PARAMETERS) {
      if (searchParams.has(key)) {
        utmParameters[key] = searchParams.get(key) as string;
      }
    }
    sessionStorage.setItem(UTM_DATA_SESSION_STORAGE_KEY, JSON.stringify(utmParameters));

    sessionStorage.removeItem(SOURCE_DATA_SESSION_STORAGE_KEY);
    if (searchParams.has(SPECIAL_LOAN_EMERGENCY_SERVICES_PARAM)) {
      sessionStorage.setItem(
        SOURCE_DATA_SESSION_STORAGE_KEY,
        JSON.stringify({ specialLoanEmergencyServices: true }),
      );
    }

    // Only persist the Google Ads click id once Google Ads Remarketing consent is granted. If
    // consent is still unknown (banner not answered yet), buffer the gclid and persist it as soon
    // as consent becomes true. If consent is denied, the gclid is never stored.
    const gclid = searchParams.get(GCLID_PARAM);
    let unsubscribe: (() => void) | undefined;
    if (gclid) {
      const consent = getGoogleAdsConsent();
      if (consent === true) {
        persistGclid(gclid);
      } else if (consent === undefined) {
        unsubscribe = onGoogleAdsConsentChange((granted) => {
          if (granted) {
            persistGclid(gclid);
          }
        });
      }
    }

    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
